import usePendingState from '../../Hooks/usePendingState';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { usePagination, useSortBy, useTable } from 'react-table';

import { faBan, faCheck, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useNotification } from '../../Contexts/NotificationContext';
import SellerItem from './SellerItem';

function SellerList() {
    const { setNotification } = useNotification();
    const [sellers, setSellers] = useState([]);
    const [batchLoading, setBatchLoading, batchPendingRef] = usePendingState();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedSellers, setSelectedSellers] = useState([]);

    const [filters, setFilters] = useState({
        nome: '',
        email: '',
        etapa_cadastro: '',
        ultimo_acesso_ha: '',
        ativo: '',
    });

    const navigate = useNavigate();

    const accessToken = localStorage.getItem('access_token');
    const baseUrl = process.env.REACT_APP_API_URL;

    const updateUrlParams = (newFilters) => {
        const filteredParams = Object.fromEntries(Object.entries(newFilters).filter(([_, value]) => value !== '' && value !== null));

        const params = new URLSearchParams(filteredParams).toString();
        navigate(`?${params}`);
    };

    const fetchSellers = async () => {
        try {
            const response = await fetch(`${baseUrl}/manager/representantes`, {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            });

            if (!response.ok) {
                throw new Error('Erro ao buscar os representantes.');
            }

            const data = await response.json();

            setSellers(data.representantes);
        } catch (error) {
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const searchParams = new URLSearchParams(window.location.search);

        for (const param of searchParams) {
            setFilters((prev) => ({
                ...prev,
                [param[0]]: param[1],
            }));
        }

        fetchSellers();
    }, []);

    const handleSelectAll = (isChecked) => {
        if (batchPendingRef.current) return;
        if (isChecked) {
            setSelectedSellers(sellers.map((seller) => seller.id));
        } else {
            setSelectedSellers([]);
        }
    };

    const isAllSelected = selectedSellers.length === sellers.length && sellers.length > 0;

    const handleSelectSeller = (sellerId) => {
        if (batchPendingRef.current) return;
        setSelectedSellers((prevSelected) =>
            prevSelected.includes(sellerId) ? prevSelected.filter((id) => id !== sellerId) : [...prevSelected, sellerId]
        );
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        const updatedFilters = {
            ...filters,
            [name]: value,
        };
        setFilters(updatedFilters);
        updateUrlParams(updatedFilters);
    };

    const runBatchAction = async (action, method, message) => {
        if (batchPendingRef.current || !selectedSellers.length) return;
        setBatchLoading(true);
        const idsString = selectedSellers.join(',');
        try {
            const response = await fetch(`${baseUrl}/manager/representantes/${action}/${idsString}`, {
                method,
                headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
            });
            if (!response.ok) throw new Error('Não foi possível concluir a operação.');
            setNotification({ type: 'success', message, show: true });
            setSelectedSellers([]);
            await fetchSellers();
        } catch (error) {
            setNotification({ type: 'error', message: error.message, show: true });
        } finally {
            setBatchLoading(false);
        }
    };

    const handleDeleteSelected = () => runBatchAction('excluir', 'DELETE', 'Registros excluídos com sucesso!');
    const handleActiveSelected = () => runBatchAction('ativar', 'PUT', 'Registros ativados com sucesso!');
    const handleDeactiveSelected = () => runBatchAction('desativar', 'PUT', 'Registros desativados com sucesso!');

    const filteredSellers = useMemo(() => {
        return sellers.filter((seller) => {
            const matchesnome = seller.nome.toLowerCase().includes(filters.nome.toLowerCase());
            const matchesativo = seller.ativo.toString().includes(filters.ativo);

            return matchesnome && matchesativo;
        });
    }, [sellers, filters]);

    const columns = useMemo(
        () => [
            {
                id: 'select',
                Header: ({ getToggleAllRowsSelectedProps }) => (
                    <input type="checkbox" checked={isAllSelected} onChange={(e) => handleSelectAll(e.target.checked)} />
                ),
                Cell: ({ row }) => (
                    <input
                        type="checkbox"
                        checked={selectedSellers.includes(row.original.id)}
                        onChange={() => handleSelectSeller(row.original.id)}
                    />
                ),
                headerClassName: 'p-4 text-left text-sm font-medium uppercase',
            },
            { Header: 'Nome', accessor: 'nome', headerClassName: 'p-2 text-left 2xl:p-4 text-sm font-medium uppercase select-none' },
            {
                Header: 'Vendas mês atual',
                accessor: 'vendas',
                headerClassName: 'p-2 text-left 2xl:p-4 text-sm font-medium uppercase select-none',
            },
            { Header: 'Ativo', accessor: 'ativo', headerClassName: 'p-2 text-left 2xl:p-4 text-sm font-medium uppercase select-none' },
            { Header: 'Ações', accessor: '', headerClassName: 'p-2 text-left 2xl:p-4 text-sm font-medium uppercase select-none' },
        ],
        [isAllSelected, selectedSellers, sellers]
    );

    const pageParams = new URLSearchParams(window.location.search);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        page,
        prepareRow,
        canPreviousPage,
        canNextPage,
        pageOptions,
        state: { pageIndex },
        gotoPage,
        nextPage,
        previousPage,
    } = useTable(
        {
            columns,
            data: filteredSellers,
            initialState: { pageIndex: pageParams.has('page') ? pageParams.get('page') - 1 : 0, pageSize: 30 },
        },
        useSortBy,
        usePagination
    );

    useEffect(() => {
        const searchParams = new URLSearchParams(window.location.search);

        if (pageIndex !== 0 || searchParams.has('page')) {
            searchParams.set('page', pageIndex + 1);
            navigate(`?${searchParams.toString()}`, { replace: true });
        }
    }, [pageIndex, navigate]);

    if (error) {
        return <div className="text-2xl text-center text-red-600 font-bold my-10">{error}</div>;
    }

    return (
        <div className="overflow-x-auto no-scrollbar relative mb-6 rounded-sm border border-stroke bg-white px-5 py-5 shadow-md">
            {loading && (
                <div className="absolute z-[100] inset-0 bg-white mx-auto py-20 flex justify-center">
                    <div className="h-16 w-16 animate-spin rounded-full border-4 border-solid border-primary border-t-transparent"></div>
                </div>
            )}

            <div className="flex items-center min-w-[40rem] mb-6">
                <h4 className="text-xl font-bold text-black">Ranking de representantes</h4>
                <Link
                    to="/manager/representantes/adicionar"
                    className="flex items-center justify-center bg-primary text-sm sm:text-base text-white ml-auto px-4 h-9 rounded transition-all hover:bg-opacity-80"
                >
                    <FontAwesomeIcon icon={faPlus} className="mr-2" />
                    Adicionar representante
                </Link>
            </div>

            <table {...getTableProps()} className="w-full min-w-[40rem] table-auto border-collapse bg-gray-2">
                <thead>
                    {headerGroups.map((headerGroup, index) => {
                        const { key, ...headerGroupProps } = headerGroup.getHeaderGroupProps();

                        return (
                            <tr key={key || index} {...headerGroupProps}>
                                {headerGroup.headers.map((column, groupIndex) => {
                                    const { key: colKey, ...columnProps } = column.getHeaderProps(
                                        column.getSortByToggleProps({ title: undefined })
                                    );

                                    return (
                                        <th key={colKey || groupIndex} {...columnProps} className={column.headerClassName}>
                                            {column.render('Header')}
                                            {column.isSorted ? (
                                                column.isSortedDesc ? (
                                                    <svg
                                                        className="fill-gray-500 inline-block mb-1 ml-1"
                                                        width="10"
                                                        height="5"
                                                        viewBox="0 0 10 5"
                                                        fill="none"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                    >
                                                        <path d="M5 0L0 5H10L5 0Z" fill=""></path>
                                                    </svg>
                                                ) : (
                                                    <svg
                                                        className="fill-gray-500 inline-block mb-1 ml-1"
                                                        width="10"
                                                        height="5"
                                                        viewBox="0 0 10 5"
                                                        fill="none"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                    >
                                                        <path d="M5 5L10 0L-4.37114e-07 8.74228e-07L5 5Z" fill=""></path>
                                                    </svg>
                                                )
                                            ) : (
                                                ''
                                            )}
                                        </th>
                                    );
                                })}
                            </tr>
                        );
                    })}

                    <tr className="bg-gray-50">
                        <td></td>
                        <td className="p-2 text-left 2xl:p-4 text-sm">
                            <div className="flex items-center space-x-2">
                                <input
                                    type="text"
                                    name="nome"
                                    placeholder="Nome"
                                    className="border border-gray-300 text-xs 2xl:text-sm p-1 w-2/3 max-w-80"
                                    value={filters.nome}
                                    onChange={handleFilterChange}
                                />
                            </div>
                        </td>
                        <td className="p-2 text-left 2xl:p-4 text-sm"></td>
                        <td className="p-2 text-left 2xl:p-4 text-sm">
                            <select
                                name="ativo"
                                className="border border-gray-300 p-1 text-xs 2xl:text-sm"
                                value={filters.ativo ? filters.ativo : 'Sim'}
                                onChange={handleFilterChange}
                            >
                                <option value="">---</option>
                                <option value="true">Sim</option>
                                <option value="false">Não</option>
                            </select>
                        </td>
                        <td className="-ml-2 py-1 text-left xl:py-4 text-sm"></td>
                        <td className="py-1 text-left xl:py-4 text-sm"></td>
                    </tr>
                </thead>
                <tbody {...getTableBodyProps()}>
                    {page.map((row, index) => {
                        prepareRow(row);
                        return (
                            <SellerItem
                                key={row.id}
                                row={row}
                                onSelect={handleSelectSeller}
                                isSelected={selectedSellers.includes(row.original.id)}
                            />
                        );
                    })}
                </tbody>
            </table>

            <div className="flex min-w-[40rem] items-center mt-6 mb-4">
                <nav className="pagination isolate inline-flex space-x-1" aria-label="Pagination">
                    <button
                        onClick={() => previousPage()}
                        disabled={!canPreviousPage}
                        className={`relative inline-flex items-center justify-center rounded w-9 h-9 ring-1 ring-inset ring-gray-300 ${canPreviousPage ? 'hover:text-white hover:bg-secondary hover:ring-secondary text-secondary' : 'text-slate-400'}`}
                    >
                        <span className="sr-only">Anterior</span>
                        <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" data-slot="icon">
                            <path
                                fillRule="evenodd"
                                d="M11.78 5.22a.75.75 0 0 1 0 1.06L8.06 10l3.72 3.72a.75.75 0 1 1-1.06 1.06l-4.25-4.25a.75.75 0 0 1 0-1.06l4.25-4.25a.75.75 0 0 1 1.06 0Z"
                                clipRule="evenodd"
                            ></path>
                        </svg>
                    </button>

                    {pageIndex !== 0 && (
                        <button
                            onClick={() => gotoPage(0)}
                            disabled={!canPreviousPage}
                            className={`relative inline-flex items-center justify-center font-medium rounded w-9 h-9 ring-1 ring-inset ring-gray-300 ${canPreviousPage ? 'hover:text-white hover:bg-secondary hover:ring-secondary text-secondary' : 'text-slate-400'}`}
                        >
                            1
                        </button>
                    )}

                    {pageIndex > 1 && pageOptions.length > 4 && (
                        <span className="relative inline-flex items-center justify-center font-medium rounded w-9 h-9 text-secondary ring-1 ring-inset ring-gray-300">
                            ...
                        </span>
                    )}

                    {pageOptions[pageIndex - 1] ? (
                        <button
                            onClick={() => gotoPage(pageIndex - 1)}
                            className="relative inline-flex items-center justify-center font-medium rounded w-9 h-9 text-secondary ring-1 ring-inset ring-gray-300"
                        >
                            {pageIndex}
                        </button>
                    ) : null}

                    <span className="relative inline-flex items-center justify-center font-medium rounded w-9 h-9 text-white bg-secondary ring-1 ring-inset ring-secondary hover:bg-slate-700">
                        {pageIndex + 1}
                    </span>

                    {pageOptions[pageIndex + 1] && (
                        <button
                            onClick={() => gotoPage(pageIndex + 1)}
                            className="relative inline-flex items-center justify-center font-medium rounded w-9 h-9 text-secondary ring-1 ring-inset ring-gray-300"
                        >
                            {pageIndex + 2}
                        </button>
                    )}

                    {pageIndex < pageOptions.length - 3 && (
                        <span className="relative inline-flex items-center justify-center font-medium rounded w-9 h-9 text-gray-400 ring-1 ring-inset ring-gray-300">
                            ...
                        </span>
                    )}

                    {pageIndex < pageOptions.length - 2 && (
                        <button
                            onClick={() => gotoPage(pageOptions.length - 1)}
                            className="relative inline-flex items-center justify-center font-medium rounded w-9 h-9 text-secondary ring-1 ring-inset ring-gray-300"
                        >
                            {pageOptions.length}
                        </button>
                    )}

                    <button
                        onClick={() => nextPage()}
                        disabled={!canNextPage}
                        className={`relative inline-flex items-center justify-center rounded w-9 h-9 ring-1 ring-inset ring-gray-300 ${canNextPage ? 'hover:text-white hover:bg-secondary hover:ring-secondary text-secondary' : 'text-slate-400'}`}
                    >
                        <span className="sr-only">Próximo</span>
                        <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" data-slot="icon">
                            <path
                                fillRule="evenodd"
                                d="M8.22 5.22a.75.75 0 0 1 1.06 0l4.25 4.25a.75.75 0 0 1 0 1.06l-4.25 4.25a.75.75 0 0 1-1.06-1.06L11.94 10 8.22 6.28a.75.75 0 0 1 0-1.06Z"
                                clipRule="evenodd"
                            ></path>
                        </svg>
                    </button>
                </nav>

                <div className="flex ml-auto gap-3" aria-busy={batchLoading}>
                    {batchLoading && <span role="status">Processando...</span>}
                    <button
                        onClick={handleDeleteSelected}
                        className="bg-red-500 text-white text-xs sm:text-sm 2xl:text-base px-3 sm:px-4 h-9 rounded ml-auto transition-all enabled:hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={batchLoading || selectedSellers.length === 0}
                    >
                        <FontAwesomeIcon icon={faTrash} className="mr-1 sm:mr-2" />
                        Excluir Selecionados
                    </button>

                    <button
                        onClick={handleActiveSelected}
                        className="bg-green-500 text-white text-xs sm:text-sm 2xl:text-base px-3 sm:px-4 h-9 rounded ml-auto transition-all enabled:hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={batchLoading || selectedSellers.length === 0}
                    >
                        <FontAwesomeIcon icon={faCheck} className="mr-1 sm:mr-2" />
                        Ativar Selecionados
                    </button>

                    <button
                        onClick={handleDeactiveSelected}
                        className="bg-yellow-500 text-white text-xs sm:text-sm 2xl:text-base px-3 sm:px-4 h-9 rounded ml-auto transition-all enabled:hover:bg-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={batchLoading || selectedSellers.length === 0}
                    >
                        <FontAwesomeIcon icon={faBan} className="mr-1 sm:mr-2" />
                        Desativar Selecionados
                    </button>
                </div>
            </div>
        </div>
    );
}

export default SellerList;
